/**
 * Collection Landing Builder
 * Section component registered through window.Sections (empire bundle).
 * - CLBFeaturedProduct: one per featured_product block; owns the gallery
 *   Flickity and the variant selectors (price / buy link / slides sync).
 * - CLBProductsCarousel: one per products block; turns the grid into a
 *   Flickity carousel on tablet & below (<= 990px) and back into a grid
 *   on desktop.
 *
 * The whole file runs inside an IIFE with a load guard: the <script> tag is
 * rendered by the section, so two Collection Landing sections on one page
 * would execute this file twice and top-level class declarations would throw.
 */

(function () {
  if (window.__clbLandingLoaded) return;
  window.__clbLandingLoaded = true;

function clbSetupFlickity({ container, cellSelector, arrowShape, buttons, freeScroll, ...settings }) {
  if (!container || typeof window.js_default !== 'function') return null;
  const options = {
    accessibility: false,
    adaptiveHeight: false,
    cellSelector: cellSelector ?? null,
    contain: true,
    margin: 5,
    cellAlign: 'center',
    friction: 0.5,
    freeScroll: !!freeScroll,
    initialIndex: 0,
    lazyLoad: false,
    percentPosition: false,
    prevNextButtons: !!buttons,
    pageDots: true,
    autoPlay: true,
    ...settings
  };
  // Passing arrowShape: null would override Flickity's built-in shape and
  // crash PrevNextButton.createSVG (null.x0); only set it when provided.
  if (arrowShape) options.arrowShape = arrowShape;
  return new (window.js_default())(container, options);
}

class CLBFeaturedProduct {
  constructor(root) {
    this.root = root;

    const configEl = root.querySelector('[data-clb-config]');
    const variantsEl = root.querySelector('[data-clb-variants]');

    this.config = configEl ? JSON.parse(configEl.textContent) : {};
    this.variants = variantsEl ? JSON.parse(variantsEl.textContent) : [];

    this.selects = Array.from(root.querySelectorAll('[data-clb-option-index]'));
    this.priceEl = root.querySelector('[data-clb-price]');
    this.buyBtn = root.querySelector('[data-clb-buy]');
    this.carouselEl = root.querySelector('[data-clb-carousel]');
    this.flkty = null;

    this.onChange = this.onChange.bind(this);
    this.selects.forEach((s) => s.addEventListener('change', this.onChange));

    this._initCarousel();
    
    // Sync price / link / gallery with the selects' initial values
    if (this.selects.length) this.onChange();
  }

  setupFlickity(opts) {
    return clbSetupFlickity(opts);
  }

  _initCarousel() {
    if (!this.carouselEl || typeof window.js_default !== 'function') return;
    this.flkty = this.setupFlickity({
      container: this.carouselEl,
      // Slides have data-image-id (not data-image); match visible slides only
      cellSelector: '.clb-featured__slide:not([hidden])',
      buttons: false,
      // Same chevron the rest of the theme uses (staticCollection, testimonials)
      arrowShape: 'M65.29 11.99L27.28 50L65.3 87.99L70.25 83.06L37.19 50L70.26 16.94L65.29 11.99Z',
      // Height comes from the carousel's CSS; measuring cells would collapse
      // to 0 because the slides use percentage heights.
      setGallerySize: true
    });
  }

  _destroyCarousel() {
    if (!this.flkty) return;
    this.flkty.destroy();
    this.flkty = null;
  }

  currentVariant() {
    if (!this.variants.length || !this.selects.length) return null;
    const selected = this.selects.map((s) => s.value);
    return this.variants.find((v) =>
      selected.every((val, i) => v.options[i] === val)
    ) || null;
  }

  formatMoney(cents) {
    const amount = (cents / 100).toFixed(2);
    const format = this.config.moneyFormat || '${{amount}}';
    return format.replace(/\{\{\s*amount[^}]*\}\}/, amount);
  }

  updateGallery(variant) {
    if (!this.carouselEl) return;

    const variantSlides = Array.from(
      this.carouselEl.querySelectorAll('.clb-featured__slide[data-variant-id]')
    );

    if (variantSlides.length) {
      // Per-variant slides: rebuild the carousel with only this variant's cells
      const hasSlides = variantSlides.some(
        (slide) => slide.dataset.variantId === String(variant.id)
      );
      this._destroyCarousel();
      variantSlides.forEach((slide) => {
        slide.hidden = hasSlides && slide.dataset.variantId !== String(variant.id);
      });
      this._initCarousel();
      return;
    }

    // Static product gallery: jump to the variant's featured image
    if (this.flkty && variant.featured_image) {
      this.flkty.selectCell(`[data-image-id="${variant.featured_image.id}"]`);
    }
  }

  onChange() {
    const variant = this.currentVariant();
    if (!variant) return;

    if (this.priceEl) this.priceEl.textContent = this.formatMoney(variant.price);
    if (this.buyBtn && this.config.productUrl) {
      this.buyBtn.href = `${this.config.productUrl}?variant=${variant.id}`;
    }
    this.updateGallery(variant);
  }

  destroy() {
    this.selects.forEach((s) => s.removeEventListener('change', this.onChange));
    this._destroyCarousel();
  }
}

class CLBProductsCarousel {
  constructor(grid) {
    this.grid = grid;
    this.flkty = null;
    this.mql = window.matchMedia('(max-width: 990px)');

    this._onBreakpoint = this._onBreakpoint.bind(this);
    if (this.mql.addEventListener) {
      this.mql.addEventListener('change', this._onBreakpoint);
    } else {
      this.mql.addListener(this._onBreakpoint);
    }

    if (this.mql.matches) this._initFlickity();
  }

  _initFlickity() {
    if (!this.grid || this.flkty) return;
    this.flkty = clbSetupFlickity({
      container: this.grid,
      cellSelector: '.clb-card',
      cellAlign: 'left',
      freeScroll: true,
      buttons: false,
      draggable: '>1'
    });
  }

  _destroyFlickity() {
    if (!this.flkty) return;
    this.flkty.destroy();
    this.flkty = null;
  }

  _onBreakpoint(e) {
    if (e.matches) {
      this._initFlickity();
    } else {
      this._destroyFlickity();
    }
  }

  destroy() {
    if (this.mql.removeEventListener) {
      this.mql.removeEventListener('change', this._onBreakpoint);
    } else {
      this.mql.removeListener(this._onBreakpoint);
    }
    this._destroyFlickity();
  }
}

class CollectionLanding {
  constructor({ el }) {
    if (!el) return;
    this.section = el;
    this.featured = Array.from(el.querySelectorAll('[data-clb-featured]')).map(
      (node) => new CLBFeaturedProduct(node)
    );
    this.productGrids = Array.from(el.querySelectorAll('[data-clb-products-grid]')).map(
      (node) => new CLBProductsCarousel(node)
    );
  }

  onSectionUnload() {
    this.featured.forEach((instance) => instance.destroy());
    this.productGrids.forEach((instance) => instance.destroy());
    this.featured = [];
    this.productGrids = [];
  }
}

document.addEventListener('DOMContentLoaded', () => {
  if (typeof window.Sections !== 'function') return;
  const sections = new window.Sections();
  sections.register('collection-landing', (section) => new CollectionLanding(section));
});

})();
